import { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import '../../styles/announcement.css'
import '../../styles/pagination.css'

import axios from "axios";
import config from '../../config';

import PageHeader from '../../components/general/pageHeader.js';
import PageTable from '../../components/general/pageTable.js';
import AddAnnouncementForm from "../../components/forms/addAnnouncementForm.js";
import EditAnnouncementForm from "../../components/forms/editAnnouncementForm.js";
import DeleteConfirmation from "../../components/general/deleteConfirmation.js";
import Loading from "../../components/general/loading.js";
import useAuthenticate from '../../components/authentication/authenticate';

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const MAnnouncements = () => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [editOverlayOpen, setEditOverlayOpen] = useState(false);
  const [addOverlayOpen, setAddOverlayOpen] = useState(false);
  const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const { user, isLoading } = useAuthenticate();
  const role = Object.keys(user?.user?.active || {})[0];
  const activeAccountId = user?.user?.active[role];

  const queryClient = useQueryClient();
  const { data: announcements = { results: [], totalPages: 0 }, isLoading: isTableLoading } = useQuery({
    queryKey: ['announcements', selectedProperty?.value, currentPage],
    queryFn: () => fetchAnnouncementsByProperties([selectedProperty?.value], currentPage, rowsPerPage),
    enabled: !!selectedProperty,
    keepPreviousData: true,
  });

  const { data: properties } = useQuery({
    queryKey: ['v2-properties'],
    queryFn: fetchProperties,
  });

  useEffect(() => {
    if (properties?.length > 0 && !selectedProperty) {
      setSelectedProperty(properties[0]);
    }
  }, [properties, selectedProperty]);

  // Reset to first page when property changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProperty]);

  const handleDelete = (announcement) => {
    setSelectedAnnouncement(announcement);
    setDeleteOverlayOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${config.API_URL}/v2/announcement/${selectedAnnouncement._id}`, {
        headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`},
      });
      setDeleteOverlayOpen(false);
      setSelectedAnnouncement(null);
      queryClient.invalidateQueries(['announcements']); // Refresh list
      toast.success("Announcement deleted successfully!");
    } catch (error) {
      console.error("Failed to delete announcement:", error);
      toast.error("Failed to delete announcement.");
    }
  };

  const totalPages = announcements?.totalPages || 1;
  const maxVisibleButtons = 10;
  const startPage = Math.floor((currentPage - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
  const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

  return (
    <div className="w-full full-page">
      <PageHeader pageName="Announcements" />
      {isLoading && !selectedProperty ? (
        <Loading />
      ) : (
        <>
          <div className="page-header">
            <Autocomplete
              options={properties}
              value={selectedProperty}
              onChange={(e, newVal) => {
                if (newVal && newVal.value && newVal.label) {
                  setSelectedProperty(newVal);
                } else {
                  setSelectedProperty(null);
                }
              }}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) =>
                typeof option === 'string'
                  ? option
                  : option?.label || option?.name || ''
              }
              renderInput={params => <TextField {...params} label="Select Property" variant="outlined" fullWidth />}
              sx={{ width: "350px" }}
            />
            <button className="header-button" onClick={(e) => { setAddOverlayOpen(true); }}>
              <i className="fas fa-add"></i>Announcement
            </button>
          </div>
          {isTableLoading ? (
            <Loading />
          ) : (
            <div className="page-content">
              <div className="table-wrapper">
                <PageTable
                  rows={announcements.results || []}
                  columns={[
                    { label: 'Title', accessor: 'announcementTitle', width: '30%' },
                    { label: 'Publish Date', accessor: (row) => new Date(row.createdAt).toLocaleDateString(), width: '10%' },
                    { label: 'Expiry Date', accessor: (row) => row.expiry ? new Date(row.expiry).toLocaleDateString() : 'N/A', width: '10%' },
                    {
                      label: 'Status',
                      accessor: (row) => {
                        const isActive = new Date(row.expiry) > new Date();
                        return (
                          <span className={`status-badge ${isActive ? 'active' : 'inactive'}`} style={{ textAlign: 'center', display: 'inline-block', width: '70px' }}>
                            {isActive ? 'Active' : 'Inactive'}
                          </span>
                        );
                      },
                      width: '10%'
                    },
                    {
                      label: 'Media',
                      accessor: (row) => {
                        if (!row.images?.length) {
                          return <i style={{ color: '#888' }}>No Image</i>;
                        }

                        const firstImage = row.images[0];
                        if (typeof firstImage === 'object' && firstImage.url) {
                          const imgUrl = `${firstImage.key}`;
                          return (
                            <button
                              className="action-buttons"
                              onClick={() => window.open(imgUrl, '_blank')}
                            >
                              <i className="fas fa-image"></i>
                            </button>
                          );
                        }

                        return <i style={{ color: '#888' }}>Image ID: {firstImage}</i>;
                      },
                      width: '10%'
                    },
                  ]}
                  hasButtons={true}
                  hasEdit={true}
                  hasDelete={true}
                  hasExpansion={true}
                  onEdit={(announcement) => {
                    setSelectedAnnouncement(announcement);
                    setEditOverlayOpen(true);
                  }}
                  onDelete={handleDelete}
                  onRowClick={(row) => {
                    setExpandedRowId(prev => (prev === row._id ? null : row._id));
                  }}
                  clickedRowId={expandedRowId}
                  renderRowExpansion={(row) => {
                    const isActive = new Date(row.expiry) > new Date();
                    return (
                      <div className="expanded-content">
                        <div className="media-content">
                          {row.images?.length > 0 ? (
                            row.images.map((photo) => (
                              <img
                                key={photo._id}
                                src={photo.key}
                                alt={photo.altText || "Announcement Photo"}
                                className="announcement-photo"
                              />
                            ))
                          ) : (
                            <p className="detail" style={{ fontStyle: 'italic', color: '#888' }}>No photos available</p>
                          )}
                        </div>
                        <div className="announcement-content">
                          <h3 style={{ textAlign: 'left', fontWeight: 'bold' }}>{row.announcementTitle}</h3>
                          <p style={{ marginTop: '10px', marginBottom: '30px' }}>
                            <span className={`status-badge ${isActive ? 'active' : 'inactive'}`} style={{ textAlign: 'center', display: 'inline-block', width: '70px' }}>
                              {isActive ? 'Active' : 'Inactive'}
                            </span>
                          </p>
                          <p><i>{new Date(row.createdAt).toLocaleDateString()}</i></p>
                          <p>{row.announcement}</p>
                        </div>
                      </div>
                    );
                  }}
                />

                {announcements.totalPages > 1 && (
                  <div className="pagination-controls">
                    <div className="pagination-buttons">
                      <button
                        className="pagination-button"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>

                      {startPage > 1 && (
                        <button
                          className="pagination-page"
                          onClick={() => setCurrentPage(startPage - 1)}
                        >
                          ...
                        </button>
                      )}

                      {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                        const pageNum = startPage + i;
                        return (
                          <button
                            key={pageNum}
                            className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      {endPage < totalPages && (
                        <button
                          className="pagination-page"
                          onClick={() => setCurrentPage(endPage + 1)}
                        >
                          ...
                        </button>
                      )}

                      <button
                        className="pagination-button"
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      {addOverlayOpen && (
        <AddAnnouncementForm
          onClose={() => {
            setAddOverlayOpen(false);
            setSelectedAnnouncement(null);
          }}
          selectedAccount={activeAccountId}
          selectedProperty={selectedProperty} />
      )}
      {editOverlayOpen && (
        <EditAnnouncementForm
          announcement={selectedAnnouncement}
          onClose={() => { setEditOverlayOpen(false); setSelectedAnnouncement(null) }}
        />
      )}
      {deleteOverlayOpen && (
        <DeleteConfirmation
          setIsOverlayOpen={setDeleteOverlayOpen}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  )
};

const fetchAnnouncementsByProperties = async (propertyIds, page = 1, limit = 5) => {
  const params = {};
  if (propertyIds?.length > 0) params.property = propertyIds[0];
  params.sortBy = 'createdAt';
  params.sortOrder = 'desc';
  params.page = page;
  params.limit = limit;

  const response = await axios.get(`${config.API_URL}/v2/announcement`, {
    headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`},
    params,
  });

  return response.data; // full paginated object
};

const fetchProperties = async () => {
  const response = await axios.get(`${config.API_URL}/v2/property`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
  return response.data.map(item => ({
    value: item._id,
    label: item.name,
  }));
};

export default MAnnouncements;
